import { useTranslation } from 'next-i18next'

import { useAppSelector } from '~/redux/hooks'
import { PageLevel } from '~/types/bootcamp'
import { ActivityMenu, LevelActionType } from '~/bootcamp/types/bootcamp.types'

import {
  selectVccHasWhyLevel,
  selectVccPageLevelsData,
  selectVccWhyShortTitle,
  selectVccLevelActivities,
} from '~/bootcamp/slices/vcc-data.slice'

const useActivityPageNavigation = (): Record<number, ActivityMenu[]> => {
  const vccLevelsPageData: PageLevel[] = useAppSelector(selectVccPageLevelsData)
  const hasWhyLevel: boolean = useAppSelector(selectVccHasWhyLevel) || false
  const whyShortTitle: string = useAppSelector(selectVccWhyShortTitle)
  const levelActivities = useAppSelector(selectVccLevelActivities)

  const { t } = useTranslation(['bootcamp', 'menu'])

  const WHY_MENU: ActivityMenu = {
    type: LevelActionType.Why,
    title: whyShortTitle || t('bootcamp:video'),
    level: '1',
  }

  const QUIZ_MENU: ActivityMenu = {
    type: LevelActionType.Quiz,
    title: t('bootcamp:menuBossBattle'),
    level: vccLevelsPageData.length + 1,
    icon: LevelActionType.Quiz,
  }

  let menu: Record<number, ActivityMenu[]> = {}

  if (hasWhyLevel) {
    menu = {
      ...menu,
      0: [WHY_MENU],
    }
  }

  let containEncyclopediaArticle = false

  vccLevelsPageData.forEach((data) => {
    const { level, regular, shortTitle, hasEncyclopedia } = data
    if (hasEncyclopedia) {
      containEncyclopediaArticle = true
    }
    if (!(level in menu)) {
      menu = {
        ...menu,
        [level]: [],
      }
    }

    const menuLevel = parseInt(level, 10)

    let levelMenu: ActivityMenu[] = [...menu[menuLevel]]

    if (!regular) {
      levelMenu = [
        ...levelMenu,
        {
          type: LevelActionType.Definition,
          title: shortTitle || (hasEncyclopedia ? t('menu:encyclopedia') : t('bootcamp:video')),
          level,
          icon: hasEncyclopedia ? LevelActionType.Encyclopedia : LevelActionType.Definition,
        },
      ]
    } else {
      levelMenu = [
        ...levelMenu,
        {
          type: LevelActionType.How,
          title: shortTitle || t('bootcamp:video'),
          level,
          icon: hasEncyclopedia ? LevelActionType.Encyclopedia : LevelActionType.How,
        },
      ]
    }

    menu = {
      ...menu,
      [level]: levelMenu,
    }
  })

  // remove the boss battle if there is an encyclopedia article
  if (!containEncyclopediaArticle) {
    menu = {
      ...menu,
      [vccLevelsPageData.length + 2]: [QUIZ_MENU],
    }
  }
  Object.entries(menu).forEach(([, item]) =>
    item[0].type === LevelActionType.Why
      ? false // Why page doesn't have any level tag
      : (item[0].levelTag = true),
  )

  const updatedMenu = Object.entries(menu).map(([level, item]) => {
    const activitiesForLevel = levelActivities[level]

    const activities = []
    // eslint-disable-next-line fp/no-loops
    for (let i = 1; i <= activitiesForLevel; i++) {
      // eslint-disable-next-line fp/no-mutating-methods
      activities.push({
        type: LevelActionType.Activity,
        title: `${t('activitySet')} ${i}`,
        level,
        index: i,
      })
    }

    item = [...item, ...activities]

    return [...item]
  })

  return { ...updatedMenu }
}

export default useActivityPageNavigation
